import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { WaitSpinner } from "../../Common/_components/WaitSpinner";
import {
    devicesActions,
    locationActions,
    locationInfoActions,
} from "../../Everou/_actions";
import { InvitationsModule } from "../../Everou/InvitationsModule/InvitationsModule";
import { Selectors } from "../../Everou/_reducers/app.reducer";

/**
 * The Everou invitations module reused as a PMS location tab.
 *
 * `InvitationsModule` takes no location prop: it reads the location from the
 * Everou slice of the store (selected uid, location/info, devices), which PMS
 * never fills — PMS keeps its own `pms` slice and only the uid is shared
 * between both (a PMS location IS an Everou location). Rather than
 * parameterising the whole invitations tree, we hydrate the Everou slice for
 * the PMS-selected location and then mount the module untouched.
 *
 * Order matters: info and devices are fetched BEFORE selecting the location, so
 * the module never mounts against a half-filled slice, and the render is gated
 * on the selected uid matching so the previous location's invitations never
 * flash while switching.
 */
export function PMSLocationInvitations() {
    // LIBRARIES
    const dispatch = useDispatch();

    // GLOBAL STATE
    const locationUid = useSelector(Selectors.getPMSLocationSelectedUid);
    const everouLocationUid = useSelector(Selectors.getSelectedLocationUid);

    // LOCAL STATE
    const [isHydrating, setIsHydrating] = useState(true);

    // INITIALIZATION
    useEffect(() => {
        if (!locationUid) return;

        let cancelled = false;
        setIsHydrating(true);

        (async () => {
            // Sequential, not parallel: both of these hit /location/info, and
            // the service caches the response by uid. Awaiting the first one
            // lets the devices fetch read that cache instead of firing a second
            // request for the same payload — and /location/info is the slow,
            // high-volume endpoint (BAC-246, BAC-165).
            await dispatch(locationInfoActions.getLocationInfo(locationUid));
            await dispatch(devicesActions.getDevicesFromLocation(locationUid));

            if (cancelled) return;

            dispatch(locationActions.selectLocation(locationUid));
            setIsHydrating(false);
        })();

        return () => {
            cancelled = true;
        };
    }, [dispatch, locationUid]);

    // VIEW
    // No location, nothing to hydrate: the effect bails out, so without this
    // the gate below would spin forever.
    if (!locationUid) return null;

    if (isHydrating || everouLocationUid !== locationUid)
        return (
            <div className="d-flex justify-content-center pt-4">
                <WaitSpinner />
            </div>
        );

    // Embedded: the tab already lives inside the location's card, so the module
    // drops its own grey canvas and card frame instead of drawing a second one.
    return <InvitationsModule isEmbedded />;
}
